import React, { useRef, useState } from 'react';
import { VenueDropdown } from './GetVenues';
import { buildForVenue, getProductionBuildInfo } from './BuildService';
import { getManifestDiff } from './ManifestService';
import { VERSION_SET_OPTIONS, VersionSetOption } from './versionSetConstants';
import { useAuthState } from '../authentication/context';
import { getEnvironment } from '../authentication/config';
import { useInterval } from '../ConferencePages/conferenceInfo/useInterval';
import { canonicalOrRawVenue } from '../utils/venueDisplay';
import { BUILD_LINK } from '../constants';
import { BuildRequestStatus } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import {
  Button,
  Container,
  Header,
  SpaceBetween,
  Grid,
  Box,
  Alert,
  Modal,
  Link,
  TextContent,
  RadioGroup,
  Checkbox,
} from '@amzn/awsui-components-console';

interface VenueInfo {
  venue: string;
  conference: string;
  // Coordinator display override; falls back to venue when unset (tcx-core-platforms-903 D2).
  canonicalVenueName?: string;
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 60;

const getVersionSetLabel = (vs: string): string =>
  VERSION_SET_OPTIONS.find((opt) => opt.value === vs)?.label ?? vs;

export const VenueList: React.FC = () => {
  const [selectedVenue, setSelectedVenue] = useState<VenueInfo | null>(null);
  const [selectedVersionSet, setSelectedVersionSet] = useState<string>('');
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [isBuilding, setIsBuilding] = useState<boolean>(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [buildSuccess, setBuildSuccess] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showManifestModal, setShowManifestModal] = useState<boolean>(false);
  const [showBuildConfirm, setShowBuildConfirm] = useState<boolean>(false);
  const [manifestLinkClicked, setManifestLinkClicked] =
    useState<boolean>(false);
  const [manifestLink, setManifestLink] = useState<string>('');
  const [isLoadingManifest, setIsLoadingManifest] = useState(false);
  const [concertRequestId, setConcertRequestId] = useState<string | null>(null);
  const [polling, setPolling] = useState<boolean>(false);
  const [pollAttempts, setPollAttempts] = useState<number>(0);
  const [buildRequestId, setBuildRequestId] = useState<string | null>(null);
  const [buildRequestStatus, setBuildRequestStatus] = useState<string | null>(
    null
  );
  const [buildSubmitError, setBuildSubmitError] = useState<string | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState<boolean>(false);
  const inFlight = useRef<boolean>(false);
  const { token } = useAuthState();

  const handleVenueSelect = (venueInfo: VenueInfo) => {
    setSelectedVenue(venueInfo);
  };

  const handleBuildClick = async () => {
    if (!selectedVenue || !selectedVersionSet || !token) return;

    setIsLoadingManifest(true);
    setBuildError(null);
    try {
      const link = await getManifestDiff(
        selectedVenue.venue,
        selectedVenue.conference,
        selectedVersionSet,
        token
      );
      setManifestLink(link);
      setShowManifestModal(true);
    } catch (error: any) {
      setBuildError(error.message || 'Failed to get manifest diff');
    } finally {
      setIsLoadingManifest(false);
    }
    setManifestLinkClicked(false);
  };

  const handleManifestConfirm = () => {
    setShowManifestModal(false);
    setShowConfirmation(true);
  };

  const handleConfirmBuild = () => {
    setShowConfirmation(false);
    setShowBuildConfirm(true);
  };

  const handleFinalBuildConfirm = async () => {
    if (!selectedVenue || !selectedVersionSet || !token) return;

    setIsBuilding(true);
    setBuildError(null);
    setBuildSuccess(false);
    setConcertRequestId(null);
    setBuildRequestId(null);
    setBuildRequestStatus(null);
    setBuildSubmitError(null);
    setPollTimedOut(false);
    setPollAttempts(0);
    setPolling(false);
    try {
      const id = await buildForVenue(
        selectedVenue.venue,
        selectedVenue.conference,
        selectedVersionSet,
        dryRun,
        token
      );
      setBuildSuccess(true);
      if (id) {
        setConcertRequestId(id);
        setPolling(true);
      }
    } catch (error: any) {
      setBuildError(error.message || 'An unexpected error occurred');
    } finally {
      setIsBuilding(false);
      setShowBuildConfirm(false);
      setManifestLinkClicked(false);
    }
  };

  useInterval(() => {
    void (async () => {
      if (!polling || !concertRequestId || !token || inFlight.current) return;

      if (pollAttempts >= MAX_POLL_ATTEMPTS) {
        setPolling(false);
        setPollTimedOut(true);
        setBuildSuccess(false);
        return;
      }
      setPollAttempts((n) => n + 1);

      inFlight.current = true;
      try {
        const res = await getProductionBuildInfo(concertRequestId, token);
        setBuildRequestStatus(res.buildRequestStatus ?? null);
        if (res.buildRequestId) {
          setBuildRequestId(res.buildRequestId);
          setPolling(false);
        } else if (
          res.buildRequestStatus === BuildRequestStatus.SUBMIT_FAILED
        ) {
          setBuildSubmitError(
            res.buildSubmitError ?? 'Build submission failed.'
          );
          setPolling(false);
          setBuildSuccess(false);
        }
      } catch (error: any) {
        if (error?.statusCode !== 404) {
          setBuildSubmitError(
            error?.message || 'Failed to fetch build status.'
          );
          setPolling(false);
          setBuildSuccess(false);
        }
      } finally {
        inFlight.current = false;
      }
    })();
  }, POLL_INTERVAL_MS);

  const handleCancelBuild = () => {
    setShowConfirmation(false);
    setShowManifestModal(false);
    setManifestLinkClicked(false);
  };

  const handleCloseManifestModal = () => {
    setShowManifestModal(false);
    setManifestLinkClicked(false);
  };

  // Display-only: show the coordinator's canonical name when set (and non-blank), else the raw venue.
  // The build and manifest-diff calls still use the raw selectedVenue.venue for routing. Routed
  // through the shared helper so a whitespace-only override falls back consistently with the tables
  // (tcx-core-platforms-903 D2 / finding on CR-301380769).
  const venueDisplayName = selectedVenue
    ? canonicalOrRawVenue(selectedVenue.canonicalVenueName, selectedVenue.venue)
    : '';

  return (
    <SpaceBetween size='l'>
      <Container
        header={
          <Header
            variant='h1'
            description='Select a venue, choose a version set, and start building'
          >
            Build venue to version set
          </Header>
        }
      >
        <SpaceBetween size='l'>
          <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
            <VenueDropdown onVenueSelect={handleVenueSelect} />
            <Button
              onClick={handleBuildClick}
              disabled={
                !selectedVenue ||
                !selectedVersionSet ||
                isBuilding ||
                isLoadingManifest
              }
              loading={isBuilding || isLoadingManifest}
              variant='primary'
            >
              {isBuilding
                ? 'Building...'
                : isLoadingManifest
                ? 'Loading manifest...'
                : 'Build'}
            </Button>
          </Grid>

          <RadioGroup
            onChange={({ detail }) => setSelectedVersionSet(detail.value)}
            value={selectedVersionSet}
            items={VERSION_SET_OPTIONS.filter(
              (opt: VersionSetOption) =>
                !opt.alphaOnly || getEnvironment() === 'alpha'
            ).map((opt) => ({
              value: opt.value,
              label: opt.label,
              description: opt.description,
            }))}
          />

          <Checkbox
            onChange={({ detail }) => setDryRun(detail.checked)}
            checked={dryRun}
          >
            Dry run
          </Checkbox>

          {selectedVenue && (
            <Container
              header={<Header variant='h2'>Selected venue</Header>}
              footer={
                <Box
                  color='text-status-info'
                  fontSize='heading-l'
                  fontWeight='bold'
                >
                  {venueDisplayName} ({selectedVenue.conference})
                </Box>
              }
            >
              <Box padding='l' textAlign='center'>
                <SpaceBetween size='s'>
                  <Box fontSize='heading-xl' color='text-label'>
                    {venueDisplayName}
                  </Box>
                  <Box fontSize='heading-m' color='text-status-info'>
                    Conference: {selectedVenue.conference}
                  </Box>
                </SpaceBetween>
              </Box>
            </Container>
          )}
        </SpaceBetween>
      </Container>

      {buildSuccess && (
        <Alert
          type='success'
          dismissible
          onDismiss={() => setBuildSuccess(false)}
        >
          Build {dryRun ? '(dry run) ' : ''}submitted successfully for{' '}
          {venueDisplayName} to {getVersionSetLabel(selectedVersionSet)}.
        </Alert>
      )}

      {concertRequestId && (
        <Container header={<Header variant='h2'>Build tracking</Header>}>
          <SpaceBetween size='s'>
            {buildRequestStatus && (
              <Box>
                <strong>Status:</strong> {buildRequestStatus}
              </Box>
            )}

            {buildRequestId ? (
              <Box>
                <strong>Build:</strong>{' '}
                <Link href={`${BUILD_LINK}${buildRequestId}`} external>
                  {buildRequestId}
                </Link>
              </Box>
            ) : buildSubmitError ? (
              <Alert type='error' header='Build submission failed'>
                {buildSubmitError}
              </Alert>
            ) : pollTimedOut ? (
              <Alert type='warning' header='Still waiting for the build'>
                The build has not reported an ID yet. Find the build&apos;s ID
                in the productionBuildInfoTable, then open it at{' '}
                <Link href={BUILD_LINK} external>
                  build.amazon.com
                </Link>
                /&lt;buildRequestId&gt;.
              </Alert>
            ) : (
              <Box>
                <SpaceBetween direction='horizontal' size='xs'>
                  <span>Waiting for build to start...</span>
                </SpaceBetween>
              </Box>
            )}
          </SpaceBetween>
        </Container>
      )}

      {buildError && (
        <Alert
          type='error'
          dismissible
          onDismiss={() => setBuildError(null)}
          header='Build error'
        >
          {buildError}
        </Alert>
      )}

      <Modal
        onDismiss={handleCloseManifestModal}
        visible={showManifestModal}
        header={`Manifest difference review — ${getVersionSetLabel(
          selectedVersionSet
        )}`}
        size='medium'
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant='link' onClick={handleCloseManifestModal}>
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={handleManifestConfirm}
                disabled={!manifestLinkClicked}
              >
                Proceed with build
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size='l'>
          <Container>
            <SpaceBetween size='m'>
              <Box textAlign='center'>
                <SpaceBetween size='xxs'>
                  <Header
                    variant='h1'
                    description='Please review the manifest difference before proceeding'
                  >
                    {venueDisplayName}
                  </Header>
                </SpaceBetween>
              </Box>

              <Alert
                type='warning'
                header='Loading issues?'
                dismissible={false}
              >
                If the manifest difference does not load, please refresh the
                manifest difference link and try again.
              </Alert>

              <Box>
                <SpaceBetween size='s'>
                  <TextContent>
                    <ol>
                      <li>
                        Click the link below to review the manifest difference
                      </li>
                      <li>Confirm that you have reviewed the changes</li>
                    </ol>
                  </TextContent>

                  <Box textAlign='center'>
                    <Link
                      href={manifestLink}
                      external
                      onFollow={() => setManifestLinkClicked(true)}
                      fontSize='heading-m'
                    >
                      Open manifest difference
                    </Link>
                  </Box>
                </SpaceBetween>
              </Box>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </Modal>

      {showConfirmation && (
        <Alert
          onDismiss={handleCancelBuild}
          header='Build confirmation'
          type='info'
          action={
            <SpaceBetween direction='horizontal' size='xs'>
              <Button onClick={handleConfirmBuild} variant='primary'>
                Confirm
              </Button>
              <Button onClick={handleCancelBuild} variant='link'>
                Cancel
              </Button>
            </SpaceBetween>
          }
        >
          Are you sure you want to build {venueDisplayName} (
          {selectedVenue?.conference}) to{' '}
          {getVersionSetLabel(selectedVersionSet)}?{dryRun && ' (Dry run)'}
        </Alert>
      )}

      {showBuildConfirm && (
        <Alert
          onDismiss={() => setShowBuildConfirm(false)}
          header='Final build confirmation'
          type='warning'
          action={
            <SpaceBetween direction='horizontal' size='xs'>
              <Button onClick={handleFinalBuildConfirm} variant='primary'>
                Confirm build
              </Button>
              <Button onClick={() => setShowBuildConfirm(false)} variant='link'>
                Cancel
              </Button>
            </SpaceBetween>
          }
        >
          Confirm build deployment for {venueDisplayName} (
          {selectedVenue?.conference}) to{' '}
          {getVersionSetLabel(selectedVersionSet)}?
          {dryRun && ' (Dry run — no packages will actually be published)'}
        </Alert>
      )}
    </SpaceBetween>
  );
};

export default VenueList;
